import { AlertTriangle, ArrowRight, ClipboardCheck, Info, ShieldAlert } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { TradingNextActionDto, TradingNextActionPriority } from 'shared';

import { Button } from '../ui/Button';
import { Card } from '../ui/Card';

const PRIORITY_PRESENTATION: Record<
  TradingNextActionPriority,
  { icon: LucideIcon; className: string }
> = {
  CRITICAL: { icon: ShieldAlert, className: 'text-danger' },
  WARNING: { icon: AlertTriangle, className: 'text-warn' },
  REVIEW: { icon: ClipboardCheck, className: 'text-primary' },
  INFO: { icon: Info, className: 'text-text-muted' },
};

interface TradingNextActionsCardProps {
  accountId: string;
  actions: TradingNextActionDto[];
  isLoading: boolean;
  isError: boolean;
  onOpenStopEditor: (symbol: string) => void;
}

function destinationPath(action: TradingNextActionDto, accountId: string): string | null {
  switch (action.destination.kind) {
    case 'POSITION_LIFECYCLE':
      return `/positions/${encodeURIComponent(action.symbol)}?accountId=${encodeURIComponent(accountId)}`;
    case 'IMPORT_HISTORY':
      return '/import';
    case 'TRADE_REVIEW':
      return `/reviews/trades/${encodeURIComponent(action.destination.tradeId)}`;
    case 'POSITION_STOP_EDITOR':
      return null;
  }
}

function destinationLabel(action: TradingNextActionDto): string {
  switch (action.destination.kind) {
    case 'POSITION_LIFECYCLE':
      return 'Open lifecycle';
    case 'POSITION_STOP_EDITOR':
      return 'Edit stop';
    case 'IMPORT_HISTORY':
      return 'Open imports';
    case 'TRADE_REVIEW':
      return 'Complete review';
  }
}

export function TradingNextActionsCard({
  accountId,
  actions,
  isError,
  isLoading,
  onOpenStopEditor,
}: TradingNextActionsCardProps) {
  return (
    <Card
      aria-label="Trading next actions"
      header={
        <div>
          <h2 className="text-lg font-semibold">Trading next actions</h2>
          <p className="text-sm text-text-muted">
            Current deterministic priorities for this swing account.
          </p>
        </div>
      }
    >
      {isLoading ? (
        <p className="text-sm text-text-muted">Loading trading actions...</p>
      ) : isError ? (
        <p className="text-sm text-danger">Could not load trading actions.</p>
      ) : actions.length === 0 ? (
        <p className="text-sm text-text-muted">
          No deterministic trading actions require attention for this account.
        </p>
      ) : (
        <div className="divide-y divide-border">
          {actions.map((action) => {
            const presentation = PRIORITY_PRESENTATION[action.priority];
            const PriorityIcon = presentation.icon;
            const path = destinationPath(action, accountId);
            const label = destinationLabel(action);
            return (
              <div
                key={action.key}
                className="grid min-w-0 gap-3 py-4 sm:grid-cols-[116px_minmax(0,1fr)_auto] sm:items-start sm:gap-4"
              >
                <div
                  className={`flex items-center gap-2 text-xs font-semibold ${presentation.className}`}
                >
                  <PriorityIcon aria-hidden="true" className="h-4 w-4 shrink-0" />
                  <span>{action.priority}</span>
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <span className="font-mono text-sm font-semibold text-primary">
                      {action.symbol}
                    </span>
                    <h3 className="text-sm font-semibold">{action.title}</h3>
                  </div>
                  <p className="mt-1 break-words text-sm text-text-muted">{action.reason}</p>
                  <p className="mt-2 break-words text-sm">{action.suggestedAction}</p>
                </div>
                {path ? (
                  <Button asChild className="w-full gap-2 sm:w-auto" size="sm" variant="secondary">
                    <Link aria-label={`${label} for ${action.symbol}`} to={path}>
                      {label}
                      <ArrowRight aria-hidden="true" className="h-4 w-4" />
                    </Link>
                  </Button>
                ) : (
                  <Button
                    aria-label={`${label} for ${action.symbol}`}
                    className="w-full gap-2 sm:w-auto"
                    size="sm"
                    type="button"
                    variant="secondary"
                    onClick={() => onOpenStopEditor(action.symbol)}
                  >
                    {label}
                    <ArrowRight aria-hidden="true" className="h-4 w-4" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
